import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import ScannerMenu from './ScannerMenu';
import AppHeader from './AppHeader';
import SignalChartModal from './SignalChartModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const volumeSparklineCache = new Map();
// Last-live-price caching removed — only real-time WebSocket prices are shown.

// Helper function to format date in NY timezone
const formatDateNY = (dateString) => {
  if (!dateString) {
    return { date: '-', time: '' };
  }

  const date = new Date(dateString);

  const datePart = date.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

  const timePart = date.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });

  return { date: datePart, time: timePart };
};

const formatCurrency = (value, fallback = '-') => {
  if (value === null || value === undefined) return fallback;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return fallback;
  return `$${numeric.toFixed(2)}`;
};

const formatVolume = (value, fallback = '-') => {
  if (value === null || value === undefined) return fallback;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return fallback;
  return numeric.toLocaleString('en-US');
};

const normalizeQuoteSymbolKey = (value) =>
  String(value || '').trim().toUpperCase().replace(/-/g, '.');

const getLiveQuoteForSymbol = (livePrices, symbol) => {
  const normalized = normalizeQuoteSymbolKey(symbol);
  const trimmed = String(symbol || '').trim();
  const upper = trimmed.toUpperCase();
  return livePrices[normalized] || livePrices[upper] || livePrices[trimmed] || null;
};

const getLivePriceValue = (liveQuote) => {
  const numeric = Number(liveQuote?.price);
  return Number.isFinite(numeric) ? numeric : null;
};

const formatVolumeTimestamp = (value) => {
  if (!value) return 'Time unavailable';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return 'Time unavailable';
  return `${dt.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })} ET`;
};

const formatTimeframeLabel = (scan) => {
  if (!scan) return 'selected timeframe';
  const period = scan.period || 'selected period';
  const interval = scan.interval || 'selected interval';
  return `${period} / ${interval}`;
};

const toVolumeBars = (candles) => {
  if (!Array.isArray(candles) || candles.length === 0) return [];
  const normalized = candles.map((candle) => {
    const volume = Number(candle?.volume);
    return {
      volume: Number.isFinite(volume) && volume > 0 ? volume : 0,
      time: candle?.time || null,
    };
  });

  const nonZero = normalized.filter((bar) => bar.volume > 0);
  const source = nonZero.length >= 5 ? nonZero : normalized;
  return source.slice(-5);
};

const VolumeSparkline = ({ scan, tableRootRef, liveQuote }) => {
  const [bars, setBars] = useState(() => volumeSparklineCache.get(scan.id) || null);
  const [loading, setLoading] = useState(!volumeSparklineCache.has(scan.id));
  const hostRef = React.useRef(null);
  const liveVolume = Number(liveQuote?.volume);
  const hasLiveVolume = Number.isFinite(liveVolume) && liveVolume > 0;

  useEffect(() => {
    let cancelled = false;

    const applyBars = (nextBars) => {
      if (cancelled) return;
      volumeSparklineCache.set(scan.id, nextBars);
      setBars(nextBars);
      setLoading(false);
    };

    const loadBars = async () => {
      if (volumeSparklineCache.has(scan.id)) {
        applyBars(volumeSparklineCache.get(scan.id));
        return;
      }

      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/scanner/signal-chart/${scan.id}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { bars: 5, _ts: Date.now() },
        });
        applyBars(toVolumeBars(response.data?.candles || []));
      } catch (err) {
        if (!cancelled) {
          volumeSparklineCache.set(scan.id, []);
          setBars([]);
          setLoading(false);
        }
      }
    };

    const rootElement = tableRootRef?.current || null;
    if (!hostRef.current || !('IntersectionObserver' in window) || !rootElement) {
      loadBars();
      return () => {
        cancelled = true;
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadBars();
          observer.disconnect();
        }
      },
      { root: rootElement, rootMargin: '120px' }
    );

    observer.observe(hostRef.current);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [scan.id, tableRootRef]);

  const displayBars = React.useMemo(() => {
    const liveTime = liveQuote?.source_timestamp || liveQuote?.timestamp || null;

    if (!bars || bars.length === 0) {
      return hasLiveVolume
        ? [{ volume: liveVolume, time: liveTime }]
        : [];
    }

    if (!hasLiveVolume) {
      return bars;
    }

    return [
      ...bars.slice(0, Math.max(0, bars.length - 1)),
      {
        volume: liveVolume,
        time: liveTime || bars[bars.length - 1]?.time || null,
      },
    ];
  }, [bars, hasLiveVolume, liveVolume, liveQuote?.source_timestamp, liveQuote?.timestamp]);

  const volumeValues = displayBars.map((bar) => Number(bar?.volume) || 0);
  const minVolume = volumeValues.length ? Math.min(...volumeValues) : 0;
  const maxVolume = volumeValues.length ? Math.max(...volumeValues) : 0;
  const volumeRange = Math.max(0, maxVolume - minVolume);

  return (
    <div ref={hostRef} className="flex flex-col items-start gap-1">
      <div className="flex items-end gap-1 h-8 w-24">
        {loading && !bars ? (
          <span className="text-[10px] text-gray-400">⏳</span>
        ) : displayBars && displayBars.length > 0 ? (
          displayBars.map((bar, index) => {
            const volume = Number(bar?.volume) || 0;
            const timeLabel = formatVolumeTimestamp(bar?.time);
            const isLiveBar = index === displayBars.length - 1 && hasLiveVolume;
            // Local range scaling makes small differences between nearby bars easier to spot.
            const normalized = volumeRange > 0 ? (volume - minVolume) / volumeRange : 0.5;
            const contrastBoost = Math.pow(Math.max(0, Math.min(1, normalized)), 0.72);
            const height = Math.round(20 + contrastBoost * 80);
            return (
              <span key={`${scan.id}-vol-${index}`} className="relative group flex items-end h-full">
                <span
                  className="w-3 bg-blue-500/75 cursor-help hover:bg-blue-600"
                  style={{ height: `${height}%` }}
                />
                <span className="pointer-events-none absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block z-30">
                  <span className="block min-w-[185px] rounded-md border border-gray-200 bg-white px-3 py-2 text-xs leading-5 text-gray-800 shadow-lg">
                    <span className="block font-semibold">{isLiveBar ? 'Live Volume' : `Bar ${index + 1}`}</span>
                    <span className="block">Volume: {formatVolume(volume)}</span>
                    <span className="block">Time: {timeLabel}</span>
                  </span>
                </span>
              </span>
            );
          })
        ) : (
          <span className="text-[10px] text-gray-400">-</span>
        )}
      </div>
      <div className="text-[10px] leading-tight text-gray-400">
        {displayBars && displayBars.length > 0 ? formatVolume(displayBars[displayBars.length - 1]?.volume) : 'No volume'}
      </div>
    </div>
  );
};

const formatAgeLabel = (ageSeconds) => {
  const seconds = Number(ageSeconds);
  if (!Number.isFinite(seconds) || seconds < 0) return 'unknown age';
  if (seconds < 60) return `${Math.round(seconds)}s old`;
  const minutes = Math.floor(seconds / 60);
  const remSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remSeconds}s old`;
};

const normalizePercentValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value.replace('%', '').trim());
    return Number.isFinite(parsed) ? parsed : null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

// CSS for flashing animation - text and cells
const flashingStyles = `
  @keyframes flash-green-text {
    0%, 100% {
      color: #10b981;
      text-shadow: 0 0 8px #10b981;
    }
    50% {
      color: #34d399;
      text-shadow: 0 0 16px #34d399;
    }
  }
  
  @keyframes flash-blue-text {
    0%, 100% {
      color: #3b82f6;
      text-shadow: 0 0 8px #3b82f6;
    }
    50% {
      color: #60a5fa;
      text-shadow: 0 0 16px #60a5fa;
    }
  }
  
  @keyframes flash-green-cell {
    0%, 100% {
      background-color: #d1fae5;
      box-shadow: inset 0 0 10px #10b981;
    }
    50% {
      background-color: #a7f3d0;
      box-shadow: inset 0 0 20px #10b981;
    }
  }
  
  @keyframes flash-green-cell-bright {
    0%, 100% {
      background-color: #a7f3d0;
      box-shadow: inset 0 0 12px #059669;
    }
    50% {
      background-color: #6ee7b7;
      box-shadow: inset 0 0 24px #059669;
    }
  }
  
  @keyframes flash-red-cell {
    0%, 100% {
      background-color: #fecaca;
      box-shadow: inset 0 0 10px #dc2626;
    }
    50% {
      background-color: #fca5a5;
      box-shadow: inset 0 0 20px #dc2626;
    }
  }
  
  .flash-green-text {
    animation: flash-green-text 1s infinite;
    font-weight: bold;
  }
  
  .flash-blue-text {
    animation: flash-blue-text 1s infinite;
    font-weight: bold;
  }
  
  .flash-green-cell {
    animation: flash-green-cell 1s infinite;
    font-weight: bold;
    color: #065f46 !important;
  }
  
  .flash-green-cell-bright {
    animation: flash-green-cell-bright 1s infinite;
    font-weight: bold;
    color: #047857 !important;
  }
  
  .flash-red-cell {
    animation: flash-red-cell 1s infinite;
    font-weight: bold;
    color: #7f1d1d !important;
  }

  .table-scrollbars {
    scrollbar-width: auto;
    scrollbar-color: #9ca3af #f3f4f6;
  }

  .table-scrollbars::-webkit-scrollbar {
    width: 12px;
    height: 12px;
  }

  .table-scrollbars::-webkit-scrollbar-track {
    background: #f3f4f6;
  }

  .table-scrollbars::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 9999px;
    border: 2px solid #f3f4f6;
  }

  .table-scrollbars::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }
`;


const ScanResultsTable = ({ onLogout, isAdmin, onShowAdmin, onShowTastytrade, onShowEarnings, onShowScanner }) => {
  const { t } = useTranslation();
  const [scanResults, setScanResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedRows, setSelectedRows] = useState(new Set()); // Track selected checkboxes
  const [livePrices, setLivePrices] = useState({}); // Store live prices by symbol
  const [wsConnected, setWsConnected] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null); // Track last refresh time
  const [tradeMessage, setTradeMessage] = useState(''); // Success/error message for trade placement
  const [tastyTrading, setTastyTrading] = useState(false); // Track tastytrade submission
  const [tastyEnv, setTastyEnv] = useState(() => {
    if (typeof window === 'undefined') return 'sandbox';
    return window.localStorage.getItem('tastytrade_env') || 'sandbox';
  });
  const [tastyOrderType, setTastyOrderType] = useState(() => {
    if (typeof window === 'undefined') return 'Limit';
    return window.localStorage.getItem('tastytrade_order_type') || 'Limit';
  });
  const [tastySettingsLoaded, setTastySettingsLoaded] = useState(false);
  const [tastyModalScan, setTastyModalScan] = useState(null);
  const [signalChartScan, setSignalChartScan] = useState(null);
  const [customAmountInput, setCustomAmountInput] = useState('');
  const [tastyModalError, setTastyModalError] = useState('');
  const [manualContractsInput, setManualContractsInput] = useState('1');
  const [deleting, setDeleting] = useState(false); // Track bulk delete in progress
  const [editingRows, setEditingRows] = useState({}); // Track edited values for executed and profit
  const [savingRows, setSavingRows] = useState(new Set()); // Track which rows are being saved
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(500);
  const [totalCount, setTotalCount] = useState(0);
  const tableScrollRef = React.useRef(null);
  
  // WebSocket reconnection state
  const [wsReconnectAttempt, setWsReconnectAttempt] = useState(0);
  const wsRef = React.useRef(null);
  const wsReconnectAttemptRef = React.useRef(0);
  const wsReconnectTimerRef = React.useRef(null);
  
  const [filters, setFilters] = useState({
    symbol: '',
    status: '',
    has_signal: '',
    signal_type: '', // New filter for Put/Call
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const wsSymbols = React.useMemo(
    () => [...new Set(scanResults.map(scan => String(scan?.symbol || '').trim().toUpperCase()).filter(Boolean))].sort(),
    [scanResults]
  );
  const wsSymbolsKey = wsSymbols.join(',');

  // Add the flashing styles to the document
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = flashingStyles;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);


  // Sync filters from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlFilters = {
      symbol: params.get('symbol') || '',
      status: params.get('status') || '',
      has_signal: params.get('has_signal') || '',
      signal_type: params.get('signal_type') || '',
    };
    
    const urlPage = parseInt(params.get('page')) || 1;
    const urlPageSize = parseInt(params.get('pageSize')) || 500;
    
    setFilters(urlFilters);
    setPage(urlPage);
    setPageSize(urlPageSize);
  }, []);

  // Update URL when filters or pagination change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.symbol) params.set('symbol', filters.symbol);
    if (filters.status) params.set('status', filters.status);
    if (filters.has_signal) params.set('has_signal', filters.has_signal);
    if (filters.signal_type) params.set('signal_type', filters.signal_type);
    if (page > 1) params.set('page', page.toString());
    if (pageSize !== 500) params.set('pageSize', pageSize.toString());
    
    const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, [filters, page, pageSize]);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('tastytrade_env', tastyEnv);
      }
    } catch (err) {
      /* noop */
    }
  }, [tastyEnv]);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('tastytrade_order_type', tastyOrderType);
      }
    } catch (err) {
      /* noop */
    }
  }, [tastyOrderType]);

  useEffect(() => {
    const fetchTastytradeSettings = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setTastySettingsLoaded(true);
          return;
        }

        const response = await axios.get(
          `${API}/users/me/tastytrade-settings`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.environment) {
          setTastyEnv(response.data.environment);
        }
        if (response.data?.default_order_type) {
          setTastyOrderType(response.data.default_order_type);
        }
      } catch (err) {
        console.error('Failed to load Tastytrade settings:', err);
      } finally {
        setTastySettingsLoaded(true);
      }
    };

    fetchTastytradeSettings();
  }, []);

  // Copy symbol to clipboard
  const copySymbol = (symbol, event) => {
    event.stopPropagation(); // Prevent row click
    navigator.clipboard.writeText(symbol).then(() => {
      // Show temporary success message
      alert(`Symbol "${symbol}" copied to clipboard!`);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  const getOptionType = (scan) => {
    const rawSignal = scan?.signal_type?.toUpperCase();
    if (rawSignal === 'CALL' || rawSignal === 'PUT') {
      return rawSignal;
    }
    const statusHint = scan?.status?.toUpperCase() || '';
    if (statusHint.includes('CALL')) return 'CALL';
    if (statusHint.includes('PUT')) return 'PUT';
    return null;
  };

  const openTastyTradeModal = (scan, event) => {
    if (event) event.stopPropagation();
    const optionType = getOptionType(scan);
    if (!optionType) {
      alert('This scan does not include a valid CALL/PUT signal.');
      return;
    }
    setTastyModalScan(scan);
    setCustomAmountInput('');
    setTastyModalError('');
    setManualContractsInput('1');
  };

  const closeTastyTradeModal = () => {
    setTastyModalScan(null);
    setCustomAmountInput('');
    setTastyModalError('');
    setManualContractsInput('1');
  };

  const openSignalChartModal = (scan, event) => {
    if (event) event.stopPropagation();
    if (!scan?.has_signal || !scan?.signal_type) {
      return;
    }
    setSignalChartScan(scan);
  };

  const closeSignalChartModal = () => {
    setSignalChartScan(null);
  };

  const handleManualContractsChange = (value) => {
    if (value === '') {
      setManualContractsInput('');
      return;
    }
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed)) {
      return;
    }
    setManualContractsInput(String(Math.max(1, parsed)));
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sorted results
  const getSortedResults = (results) => {
    // First, add calculated gain_percent to each result
    const resultsWithGain = results.map(scan => {
      let gain_percent = null;
      if (scan.entry_price && scan.target_price) {
        // For CALL: profit = target > entry → positive %
        // For PUT:  profit = price falls to target (target < entry) → flip sign so winning PUT shows positive
        gain_percent = scan.signal_type === 'PUT'
          ? ((scan.entry_price - scan.target_price) / scan.entry_price) * 100
          : ((scan.target_price - scan.entry_price) / scan.entry_price) * 100;
      }
      return { ...scan, gain_percent };
    });

    if (!sortConfig.key) return resultsWithGain;

    const sorted = [...resultsWithGain].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle live prices for sorting
      if (sortConfig.key === 'live_price') {
        aVal = getLivePriceValue(
          getLiveQuoteForSymbol(livePrices, a.symbol)
        ) || 0;
        bVal = getLivePriceValue(
          getLiveQuoteForSymbol(livePrices, b.symbol)
        ) || 0;
      }

      // Handle null/undefined values
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Boolean comparison (for executed field)
      if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        // false (0) before true (1) for ascending, opposite for descending
        const aNum = aVal ? 1 : 0;
        const bNum = bVal ? 1 : 0;
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Numeric comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // String comparison
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  // Sort icon component
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <span className="text-gray-400 ml-1">⇅</span>;
    }
    return sortConfig.direction === 'asc' 
      ? <span className="text-blue-600 ml-1">↑</span>
      : <span className="text-blue-600 ml-1">↓</span>;
  };

  useEffect(() => {
    fetchScanResults();
  }, []);

  // WebSocket connection for live prices with auto-reconnect
  useEffect(() => {
    if (wsSymbols.length === 0) {
      // Clean up WebSocket if no results
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setWsConnected(false);
      return;
    }

    const symbols = wsSymbols;

    // Check if symbols changed
    const currentSymbols = wsRef.current?.symbols || [];
    const symbolsChanged = JSON.stringify([...symbols].sort()) !== JSON.stringify([...currentSymbols].sort());
    
    // If WebSocket exists and symbols haven't changed, just update subscription
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && !symbolsChanged) {
      return; // No need to reconnect
    }
    
    // If symbols changed and connection is open, send new subscription
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && symbolsChanged) {
      wsRef.current.symbols = symbols;
      wsRef.current.send(JSON.stringify({
        symbols: symbols,
        interval: 5
      }));
      return;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }
    if (wsReconnectTimerRef.current) {
      clearTimeout(wsReconnectTimerRef.current);
      wsReconnectTimerRef.current = null;
    }

    // WebSocket URL
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws/prices';

    const applyQuotes = (quotes) => {
      if (!Array.isArray(quotes) || quotes.length === 0) return;

      const mappedQuotes = {};
      quotes.forEach((quote) => {
        const normalizedSymbol = normalizeQuoteSymbolKey(quote?.symbol);
        if (!normalizedSymbol) return;
        mappedQuotes[normalizedSymbol] = {
          price: quote.price,
          change_percent: quote.change_percent,
          volume: quote.volume,
          timestamp: quote.timestamp,
          source_timestamp: quote.source_timestamp,
          source_delay_seconds: quote.source_delay_seconds,
          is_delayed: quote.is_delayed,
          source: quote.source,
        };
      });

      if (Object.keys(mappedQuotes).length === 0) return;

      setLivePrices((prev) => ({ ...prev, ...mappedQuotes }));
    };

    const requestQuoteSnapshot = async (targetSymbols) => {
      const cleanSymbols = [...new Set((targetSymbols || []).map((s) => String(s || '').trim().toUpperCase()).filter(Boolean))];
      if (cleanSymbols.length === 0) return;

      try {
        const response = await axios.get(`${API}/ws/prices/quote-snapshot`, {
          params: {
            symbols: cleanSymbols.join(','),
            max_symbols: Math.min(cleanSymbols.length, 1000),
          },
        });
        applyQuotes(response.data?.quotes || []);
      } catch (err) {
        console.error('Quote snapshot fallback failed:', err);
      }
    };
    
    const connectWebSocket = () => {
      const ws = new WebSocket(wsUrl);
      ws.symbols = symbols; // Store symbols on WebSocket object
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('✅ WebSocket connected');
        setWsConnected(true);
        setWsReconnectAttempt(0); // Reset reconnection attempts
        wsReconnectAttemptRef.current = 0;
        
        // Subscribe to symbols; snapshot will be requested on the 'subscribed' ack.
        ws.send(JSON.stringify({
          symbols: symbols,
          interval: 5
        }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'error') {
            console.error('WebSocket subscription error:', data.message);
            setWsConnected(false);
            return;
          }

          if (data.type === 'ping') {
            ws.send(JSON.stringify({ type: 'pong' }));
            return;
          }

          if (data.type === 'subscribed') {
            requestQuoteSnapshot(Array.isArray(data.symbols) ? data.symbols : symbols);
            return;
          }
          
          if (data.type === 'price_update') {
            const normalizedSymbol = normalizeQuoteSymbolKey(data.symbol);
            if (!normalizedSymbol) return;
            const nextQuote = {
              price: data.price,
              change_percent: data.change_percent,
              volume: data.volume,
              timestamp: data.timestamp,
              source_timestamp: data.source_timestamp,
              source_delay_seconds: data.source_delay_seconds,
              is_delayed: data.is_delayed,
              source: data.source
            };
            setLivePrices(prev => ({
              ...prev,
              [normalizedSymbol]: nextQuote
            }));
            return;
          }

          if (data.type === 'price_batch_update' && Array.isArray(data.quotes)) {
            applyQuotes(data.quotes);
          }
        } catch (err) {
          console.error('WebSocket message error:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setWsConnected(false);
        
        // Auto-reconnect with exponential backoff and jitter
        if (symbols.length > 0) {
          const attempt = wsReconnectAttemptRef.current;
          const baseDelay = Math.min(1000 * Math.pow(2, attempt), 30000); // Max 30 seconds
          const jitter = Math.floor(Math.random() * Math.max(250, Math.floor(baseDelay * 0.2)));
          const delay = baseDelay + jitter;
          
          console.log(`Reconnecting WebSocket in ${delay/1000}s (attempt ${attempt + 1})...`);

          wsReconnectAttemptRef.current = attempt + 1;
          setWsReconnectAttempt(wsReconnectAttemptRef.current);

          wsReconnectTimerRef.current = setTimeout(() => {
            connectWebSocket();
          }, delay);
        }
      };
    };

    connectWebSocket();

    // Cleanup on unmount
    return () => {
      if (wsReconnectTimerRef.current) {
        clearTimeout(wsReconnectTimerRef.current);
        wsReconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [wsSymbolsKey, BACKEND_URL]);

  const fetchScanResults = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      // Pagination parameters
      params.append('limit', pageSize.toString());
      params.append('offset', ((page - 1) * pageSize).toString());
      
      // Filter parameters
      if (filters.symbol) params.append('symbol', filters.symbol);
      if (filters.status) params.append('status', filters.status);
      if (filters.has_signal) params.append('has_signal', filters.has_signal);
      if (filters.signal_type) params.append('signal_type', filters.signal_type);

      const response = await axios.get(`${API}/scanner/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setScanResults(response.data.results || []);
      setTotalCount(response.data.total_count || 0);
      setLastUpdated(new Date()); // Set last updated time
    } catch (err) {
      setError((Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || 'Failed to fetch scan results');
      if (err.response?.status === 401) {
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch when page or pageSize changes
  useEffect(() => {
    fetchScanResults();
  }, [page, pageSize]);

  // State for tracking which scan is being verified
  const [verifyingScans, setVerifyingScans] = useState(new Set());

  // Verify individual scan result
  const handleVerifyOne = async (scanId, e) => {
    e.stopPropagation(); // Prevent row click
    
    // Add to verifying set
    setVerifyingScans(prev => new Set([...prev, scanId]));
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API}/scanner/verify`, 
        { scan_result_id: scanId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update only this specific scan result in state
      setScanResults(prev => prev.map(scan => 
        scan.id === scanId 
          ? { 
              ...scan, 
              status: response.data.status,
              status_datetime: response.data.status_datetime
            }
          : scan
      ));
      
      // Show success message with symbol and new status
      const scan = scanResults.find(s => s.id === scanId);
      alert(`✅ ${scan?.symbol} (ID: ${scanId}) verified!\n\nStatus: ${response.data.status}\n\n${response.data.reason}`);
      
    } catch (err) {
      const scan = scanResults.find(s => s.id === scanId);
      alert(`❌ Failed to verify ${scan?.symbol} (ID: ${scanId}):\n${(Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || err.message}`);
      setError((Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || 'Failed to verify scan result');
    } finally {
      // Remove from verifying set
      setVerifyingScans(prev => {
        const newSet = new Set(prev);
        newSet.delete(scanId);
        return newSet;
      });
    }
  };

  const handleRemove = async (scanId, e) => {
    e.stopPropagation(); // Prevent row click when clicking remove button
    
    if (!window.confirm('Are you sure you want to remove this scan result?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/scanner/${scanId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Remove from local state immediately
      setScanResults(prev => prev.filter(scan => scan.id !== scanId));
      
    } catch (err) {
      alert((Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || 'Failed to remove scan result');
    }
  };

  // Verify all positions showing in the table
  const handleVerifyAll = async () => {
    if (!window.confirm(`Verify all ${scanResults.length} positions in the table?`)) {
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const scanIds = scanResults.map(scan => scan.id);
      
      const response = await axios.post(`${API}/scanner/verify-all`, 
        { scan_ids: scanIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(`Verified ${response.data.verified_count} positions successfully!`);
      
      // Refresh the table
      fetchScanResults();
    } catch (err) {
      setError((Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || 'Failed to verify positions');
    } finally {
      setLoading(false);
    }
  };

  // Archive expired positions
  const handleArchiveExpired = async () => {
    // Count expired and invalid positions
    const cleanableCount = scanResults.filter(scan => 
      scan.status === 'EXPIRED' || scan.status === 'INVALID'
    ).length;
    
    if (cleanableCount === 0) {
      alert('No expired or invalid positions found in the current view.');
      return;
    }

    if (!window.confirm(`Remove ${cleanableCount} expired/invalid position(s)? This will permanently delete them from the database.`)) {
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const cleanableIds = scanResults
        .filter(scan => scan.status === 'EXPIRED' || scan.status === 'INVALID')
        .map(scan => scan.id);
      
      const response = await axios.post(`${API}/scanner/archive-expired`, 
        { scan_ids: cleanableIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(`Removed ${response.data.archived_count} expired/invalid position(s) successfully!`);
      
      // Refresh the table
      fetchScanResults();
    } catch (err) {
      setError((Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || 'Failed to remove expired/invalid positions');
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceTastyTrade = async () => {
    if (!tastyModalScan) return;

    let customAmount = null;
    if (customAmountInput.trim().length > 0) {
      customAmount = parseFloat(customAmountInput);
      if (Number.isNaN(customAmount) || customAmount <= 0) {
        setTastyModalError('Enter a positive custom dollar amount.');
        return;
      }
    } else if (modalEntryPrice) {
      customAmount = scannerContracts * modalEntryPrice;
    }

    setTastyTrading(true);
    setTradeMessage('');
    setTastyModalError('');

    try {
      const token = localStorage.getItem('token');
      const payload = {
        scan_result_id: tastyModalScan.id,
        environment: tastyEnv,
        order_type: tastyOrderType,
      };
      if (customAmount) {
        payload.custom_amount = customAmount;
      }

      const response = await axios.post(
        `${API}/tastytrade/trade`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const successMessage =
        `✅ Tastytrade (${tastyEnv}) order submitted for ${response.data.symbol} ` +
        `(qty ${response.data.quantity}).`;
      setTradeMessage(successMessage.trim());
      closeTastyTradeModal();
      setTimeout(() => setTradeMessage(''), 10000);
    } catch (err) {
      const errorMsg = (Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || 'Failed to place Tastytrade order';
      setTastyModalError(errorMsg);
      setTradeMessage(`❌ Tastytrade error: ${errorMsg}`);
      setTimeout(() => setTradeMessage(''), 8000);
    } finally {
      setTastyTrading(false);
    }
  };


  const handleScanComplete = () => {
    // Refresh the results table when a new scan is completed
    fetchScanResults();
  };

  const modalSymbolKey = tastyModalScan ? normalizeQuoteSymbolKey(tastyModalScan.symbol) : '';
  const modalQuote = tastyModalScan
    ? (getLiveQuoteForSymbol(livePrices, tastyModalScan.symbol) || livePrices[modalSymbolKey])
    : null;
  const modalLivePrice = getLivePriceValue(modalQuote);
  const modalDisplayLivePrice = modalLivePrice;
  const modalLiveVolume = modalQuote?.volume;
  const modalFallbackPrice = tastyModalScan?.last_close;
  const modalOptionType = tastyModalScan ? getOptionType(tastyModalScan) : null;
  const modalEntryPrice = tastyModalScan
    ? (tastyModalScan.entry_price ?? modalLivePrice ?? tastyModalScan.last_close ?? null)
    : null;
  const modalTimeframeLabel = formatTimeframeLabel(tastyModalScan);
  const parsedManualContracts = (() => {
    const parsed = parseInt(manualContractsInput, 10);
    if (Number.isNaN(parsed) || parsed <= 0) {
      return 1;
    }
    return parsed;
  })();
  const scannerContracts = parsedManualContracts;
  const parsedCustomAmount = (() => {
    if (!customAmountInput.trim()) return null;
    const parsed = parseFloat(customAmountInput);
    return Number.isNaN(parsed) ? null : parsed;
  })();
  const modalDerivedQuantity = parsedCustomAmount && modalEntryPrice
    ? Math.max(1, Math.floor(parsedCustomAmount / modalEntryPrice))
    : scannerContracts;
  const modalLimitPrice = tastyOrderType === 'Limit' ? modalEntryPrice : null;
  const modalPriceForEstimates = modalLimitPrice ?? modalLivePrice ?? modalEntryPrice ?? null;
  const modalNotional = modalPriceForEstimates && modalDerivedQuantity
    ? modalPriceForEstimates * modalDerivedQuantity
    : null;
  const modalOrderAction = modalOptionType === 'PUT' ? 'Sell to Open' : 'Buy to Open';
  const modalPriceEffect = modalOrderAction.startsWith('Sell') ? 'Credit' : 'Debit';
  const modalContractMultiplier = 100;

  // Format last updated time
  const formatLastUpdated = () => {
    if (!lastUpdated) return '';
    
    const now = new Date();
    const diffMs = now - lastUpdated;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    
    if (diffSecs < 10) return 'Just now';
    if (diffSecs < 60) return `${diffSecs}s ago`;
    if (diffMins < 60) return `${diffMins}m ago`;
    
    return lastUpdated.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getStatusBadge = (status) => {
    if (!status) return <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-600">N/A</span>;
    
    const colors = {
      'PENDING': 'bg-yellow-100 text-yellow-800',
      'ENTER_NOW': 'bg-green-100 text-green-800',
      'TARGET_HIT': 'bg-blue-100 text-blue-800',
      'STOPPED': 'bg-red-100 text-red-800',
      'EXPIRED': 'bg-gray-100 text-gray-800',
      'INVALID': 'bg-red-100 text-red-800',
    };
    
    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
        {status}
      </span>
    );
  };

  // Checkbox selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Select all visible rows
      const allIds = new Set(scanResults.map(scan => scan.id));
      setSelectedRows(allIds);
    } else {
      // Deselect all
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (scanId) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(scanId)) {
      newSelected.delete(scanId);
    } else {
      newSelected.add(scanId);
    }
    setSelectedRows(newSelected);
  };

  // Handle executed checkbox change (stores in local state, not saved until Save button clicked)
  const handleExecutedChange = (scanId, executed) => {
    setEditingRows(prev => ({
      ...prev,
      [scanId]: {
        ...prev[scanId],
        executed: executed
      }
    }));
  };

  // Handle profit input change (stores in local state, not saved until Save button clicked)
  const handleProfitChange = (scanId, profit) => {
    setEditingRows(prev => ({
      ...prev,
      [scanId]: {
        ...prev[scanId],
        profit: profit
      }
    }));
  };

  // Save execution details (executed status and profit)
  const handleSaveExecution = async (scanId, scan) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        alert('You must be logged in to save execution details');
        return;
      }

      // Get values from editing state or original scan values
      const editedData = editingRows[scanId] || {};
      const executed = editedData.executed !== undefined ? editedData.executed : scan.executed;
      const profitValue = editedData.profit !== undefined ? editedData.profit : scan.profit;

      // Validate profit is a number if provided
      let profitNumber = null;
      if (profitValue !== null && profitValue !== undefined && profitValue !== '') {
        profitNumber = parseFloat(profitValue);
        if (isNaN(profitNumber)) {
          alert('Please enter a valid number for profit');
          return;
        }
      }

      // Mark row as saving
      setSavingRows(prev => new Set(prev).add(scanId));

      // Update backend
      const response = await axios.put(
        `${API}/scanner/executed`,
        {
          scan_result_id: scanId,
          executed: executed,
          profit: profitNumber
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.status === 200) {
        // Update local state
        setScanResults(prevResults =>
          prevResults.map(s =>
            s.id === scanId
              ? { ...s, executed: executed, profit: profitNumber }
              : s
          )
        );
        
        // Clear editing state for this row
        setEditingRows(prev => {
          const newState = { ...prev };
          delete newState[scanId];
          return newState;
        });
        
        console.log(`✅ Saved execution details for scan ${scanId}`);
      }
    } catch (err) {
      console.error('Error saving execution details:', err);
      alert(`Error saving execution details: ${(Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || err.message}`);
    } finally {
      // Remove from saving state
      setSavingRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(scanId);
        return newSet;
      });
    }
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) {
      alert('Please select at least one row to delete');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedRows.size} scan result(s)? This will mark them as inactive.`
    );

    if (!confirmDelete) return;

    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/scanner/delete-multiple`,
        Array.from(selectedRows),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Success
      alert(`✅ Successfully deleted ${response.data.deleted_count} scan result(s)`);
      
      // Clear selection
      setSelectedRows(new Set());
      
      // Refresh the table
      await fetchScanResults();
    } catch (err) {
      console.error('Bulk delete error:', err);
      alert(`❌ Error deleting scan results: ${(Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="h-screen bg-gray-100 max-w-full flex flex-col overflow-x-hidden">
      <AppHeader
        title={null}
        isAdmin={isAdmin}
        onShowScanner={onShowScanner}
        onShowEarnings={onShowEarnings}
        onShowTastytrade={onShowTastytrade}
        onShowAdmin={onShowAdmin}
        onLogout={onLogout}
      />
      <div className="flex flex-1 overflow-hidden max-w-full">
        {/* Left Sidebar - Scanner Menu */}
        <ScannerMenu onScanComplete={handleScanComplete} />

        {/* Main Content */}
        <div className="flex-1 w-full max-w-full min-w-0 overflow-hidden flex flex-col min-h-0">
          {/* Main Content Area */}
          <div className="flex-1 p-3 sm:p-6 max-w-full min-w-0 flex flex-col min-h-0 overflow-hidden">
        <div className="flex-none">
        <div className="mb-2 sm:mb-4">
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">
              {t('resistance_scanner_label')}
            </h2>
          </div>
          {/* Filters */}
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow mb-4 sm:mb-6 max-w-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  {t('symbol')}
                </label>
                <input
                  type="text"
                  value={filters.symbol}
                  onChange={(e) => setFilters({ ...filters, symbol: e.target.value.toUpperCase() })}
                  placeholder="e.g., NVDA"
                  className="w-full px-2 sm:px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Put/Call
                </label>
                <select
                  value={filters.signal_type}
                  onChange={(e) => setFilters({ ...filters, signal_type: e.target.value })}
                  className="w-full px-2 sm:px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('all')}</option>
                  <option value="CALL">Call</option>
                  <option value="PUT">Put</option>
                </select>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  {t('status')}
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full px-2 sm:px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('all')}</option>
                  <option value="PENDING">{t('pending')}</option>
                  <option value="ENTER_NOW">{t('enter_now')}</option>
                  <option value="TARGET_HIT">{t('target_hit')}</option>
                  <option value="STOPPED">{t('stopped')}</option>
                  <option value="EXPIRED">{t('expired')}</option>
                </select>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  {t('has_signal')}
                </label>
                <select
                  value={filters.has_signal}
                  onChange={(e) => setFilters({ ...filters, has_signal: e.target.value })}
                  className="w-full px-2 sm:px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('all')}</option>
                  <option value="1">{t('yes')}</option>
                  <option value="0">{t('no')}</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={fetchScanResults}
                  disabled={loading}
                  className="w-full bg-blue-600 text-white px-3 sm:px-4 py-2 text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition"
                >
                  {loading ? t('loading') + '...' : '🔍 ' + t('search')}
                </button>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 text-red-800 p-3 sm:p-4 rounded-lg mb-4 sm:mb-6 text-sm">
              {error}
            </div>
          )}

          {/* Trade Message */}
          {tradeMessage && (
            <div className={`p-3 sm:p-4 rounded-lg mb-4 sm:mb-6 text-sm whitespace-pre-line ${
              tradeMessage.includes('✅') 
                ? 'bg-green-100 text-green-800 border border-green-300' 
                : 'bg-red-100 text-red-800 border border-red-300'
            }`}>
              {tradeMessage}
            </div>
          )}

        </div>


          {/* Results Count with Action Buttons */}
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="mb-3 sm:mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 flex-none">
            <div>
              <p className="text-sm sm:text-base text-gray-600">
                Showing <span className="font-bold">{scanResults.length}</span> of <span className="font-bold">{totalCount}</span> {t('scan_results')}
                {totalCount > 0 && (
                  <span className="text-xs text-gray-500 ml-2">
                    (Page {page} of {Math.ceil(totalCount / pageSize)})
                  </span>
                )}
              </p>
              {lastUpdated && (
                <p className="text-xs text-gray-500 mt-1">
                  {t('last_updated')}: {formatLastUpdated()}
                </p>
              )}
            </div>
            
            {/* Action Buttons Group */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handleVerifyAll}
                disabled={loading || scanResults.length === 0}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
                title="Verify all positions in table"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              
              <button
                onClick={handleArchiveExpired}
                disabled={loading || scanResults.length === 0}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 disabled:bg-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
                title="Remove all expired and invalid positions from database"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
              
              {selectedRows.size > 0 && (
                <button
                  onClick={handleBulkDelete}
                  disabled={deleting}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:bg-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
                  title="Delete selected rows"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span className="text-xs">{selectedRows.size}</span>
                </button>
              )}
              
              <button
                onClick={fetchScanResults}
                disabled={loading}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
                title="Refresh table data"
              >
                <svg 
                  className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Table Container - Mobile Optimized */}
          <div className="flex-1 min-h-0 flex flex-col">
          <div className="bg-white rounded-lg shadow flex-1 min-h-0 flex flex-col overflow-hidden"> 
            {/* Mobile: Horizontal Scroll Hint */}
            <div className="lg:hidden bg-blue-50 px-3 py-2 text-xs text-blue-700 text-center flex-none">
              {t('swipe_hint')}
            </div>
            
            {/* Table Wrapper with Touch-Friendly Scroll */}
            <div
              ref={tableScrollRef}
              className="table-scrollbars flex-1 min-h-0 overflow-scroll w-full"
              style={{ WebkitOverflowScrolling: 'touch', scrollbarGutter: 'stable both-edges' }}
            >
              <table className="w-full min-w-max divide-y divide-gray-200">
                <thead className="bg-gray-50 text-xs sticky top-0 z-20">
                  <tr>
                    <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        title="Select/Deselect all rows">
                      <input
                        type="checkbox"
                        checked={selectedRows.size === scanResults.length && scanResults.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                      />
                    </th>
                    <th 
                      onClick={() => handleSort('id')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Unique scan result identifier"
                    >
                      {t('id')} <SortIcon columnKey="id" />
                    </th>
                    <th 
                      onClick={() => handleSort('symbol')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Stock ticker symbol (e.g., AAPL, TSLA)"
                    >
                      {t('symbol')} <SortIcon columnKey="symbol" />
                    </th>
                    <th 
                      onClick={() => handleSort('signal_type')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="CALL = Bullish signal (expect price to rise) | PUT = Bearish signal (expect price to fall)"
                    >
                      {t('put_call')} <SortIcon columnKey="signal_type" />
                    </th>
                    <th 
                      onClick={() => handleSort('live_price')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Real-time market price from WebSocket feed"
                    >
                      {t('live_price')} <SortIcon columnKey="live_price" />
                    </th>
                    <th
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      title="Last 5 volume bars for the selected period and interval"
                    >
                      Volume
                    </th>
                    <th 
                      onClick={() => handleSort('entry_price')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Recommended entry price to open position"
                    >
                      {t('entry')} <SortIcon columnKey="entry_price" />
                    </th>
                    <th 
                      onClick={() => handleSort('stop_price')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Stop-loss price to limit losses if trade goes wrong"
                    >
                      {t('stop')} <SortIcon columnKey="stop_price" />
                    </th>
                    <th 
                      onClick={() => handleSort('target_price')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Target price to take profit"
                    >
                      {t('target')} <SortIcon columnKey="target_price" />
                    </th>
                    <th 
                      onClick={() => handleSort('gain_percent')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Formula: (Target - Entry) / Entry × 100 | Potential gain % if target is reached"
                    >
                      Gain % <SortIcon columnKey="gain_percent" />
                    </th>
                    <th 
                      onClick={() => handleSort('risk_reward')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Formula: (Target - Entry) / (Entry - Stop) | Risk/Reward ratio. Higher is better (e.g., 2.5 = $2.50 gain per $1 risk)"
                    >
                      {t('risk_reward')} <SortIcon columnKey="risk_reward" />
                    </th>
                    <th 
                      onClick={() => handleSort('status')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Current signal status: PENDING = Wait | ENTER_NOW = Enter position | TARGET_HIT = Profit taken | STOPPED = Loss taken | EXPIRED = Signal expired"
                    >
                      {t('status')} <SortIcon columnKey="status" />
                    </th>
                    <th 
                      onClick={() => handleSort('scan_timestamp')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Date and time when this scan result was generated (NY timezone)"
                    >
                      {t('scan_date')} <SortIcon columnKey="scan_timestamp" />
                    </th>
                    <th 
                      onClick={() => handleSort('executed')}
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      title="Sort by executed status - Click to mark this trade as executed"
                    >
                      Executed? <SortIcon columnKey="executed" />
                    </th>
                    <th 
                      className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      title="Enter profit/loss amount for this trade"
                    >
                      Profit ($)
                    </th>
                    <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        title="Available actions: Trade (place IB order) | Remove (delete from view)">
                      {t('actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="16" className="px-2 sm:px-3 py-8 sm:py-12 text-center">
                        <div className="flex justify-center">
                          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600"></div>
                        </div>
                      </td>
                    </tr>
                  ) : scanResults.length === 0 ? (
                    <tr>
                      <td colSpan="16" className="px-2 sm:px-3 py-8 sm:py-12 text-center text-gray-500 text-sm">
                        {t('no_scan_results')}
                      </td>
                    </tr>
                  ) : (
                    getSortedResults(scanResults).map((scan) => {
                      const symbolKey = String(scan?.symbol || '').trim().toUpperCase();
                      const normalizedSymbolKey = normalizeQuoteSymbolKey(symbolKey);
                      const livePrice = getLiveQuoteForSymbol(livePrices, scan.symbol)
                        || livePrices[normalizedSymbolKey]
                        || livePrices[symbolKey];
                      const displayLivePrice = livePrice;
                      const livePriceValue = getLivePriceValue(livePrice);
                      const displayLivePriceValue = livePriceValue;
                      const fallbackPrice = scan?.last_close;
                      const changePercent = (displayLivePriceValue !== null && scan.entry_price)
                        ? ((displayLivePriceValue - scan.entry_price) / scan.entry_price) * 100
                        : normalizePercentValue(displayLivePrice?.change_percent);
                      const quoteAgeLabel = formatAgeLabel(displayLivePrice?.source_delay_seconds);
                      const scanDate = formatDateNY(scan.scan_timestamp);
                      
                      return (
                        <tr
                          key={scan.id}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedRows.has(scan.id)}
                              onChange={() => handleSelectRow(scan.id)}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                            />
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                            {scan.id}
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                            <span className="text-xs sm:text-sm font-semibold text-blue-600">
                              {scan.symbol}
                            </span>
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                            {scan.has_signal && scan.signal_type ? (
                              <button
                                type="button"
                                onClick={(event) => openSignalChartModal(scan, event)}
                                title={`Open signal chart (${scan.interval || '5m'} candles)`}
                                className={`px-2 py-1 rounded text-xs font-semibold hover:opacity-85 transition ${
                                  scan.signal_type === 'CALL'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {scan.signal_type === 'CALL' ? '📈 Call' : '📉 Put'}
                              </button>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                            {displayLivePriceValue !== null ? (
                              <div className="flex flex-col">
                                <span className="text-xs sm:text-sm font-bold text-gray-900">
                                  ${displayLivePriceValue.toFixed(2)}
                                </span>
                                <span className={`text-[10px] ${displayLivePrice?.is_delayed ? 'text-amber-600' : 'text-emerald-600'}`}>
                                  {displayLivePrice?.is_delayed ? `Delayed (${quoteAgeLabel})` : `Fresh (${quoteAgeLabel})`}
                                </span>
                                {changePercent !== null && changePercent !== undefined && (
                                  <span className={`text-xs ${changePercent >= 0 ? 'text-green-600' : 'text-red-600'}`} title="vs entry price">
                                    {changePercent >= 0 ? '▲' : '▼'} {changePercent > 0 ? '+' : ''}{changePercent.toFixed(2)}%
                                  </span>
                                )}
                              </div>
                            ) : (
                              wsConnected ? (
                                <div className="flex flex-col">
                                  <span className="text-xs text-gray-400">⏳ Loading live...</span>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">⚠️ Disconnected</span>
                              )
                            )}
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap align-top">
                            <VolumeSparkline scan={scan} tableRootRef={tableScrollRef} liveQuote={displayLivePrice} />
                          </td>
                          <td className={`px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm ${
                            livePrice && scan.entry_price ? (
                              scan.signal_type === 'CALL'
                                ? (scan.target_price && livePrice.price > scan.entry_price && livePrice.price < scan.target_price ? 'flash-green-cell' : 'text-gray-900')
                                : (scan.target_price && livePrice.price < scan.entry_price && livePrice.price > scan.target_price ? 'flash-red-cell' : 'text-gray-900')
                            ) : 'text-gray-900'
                          }`}>
                            {scan.entry_price ? `$${scan.entry_price.toFixed(2)}` : '-'}
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-red-600">
                            {scan.stop_price ? `$${scan.stop_price.toFixed(2)}` : '-'}
                          </td>
                          <td className={`px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm ${
                            livePrice && scan.target_price && (
                              scan.signal_type === 'CALL'
                                ? (livePrice.price > scan.target_price ? 'flash-green-cell-bright' : 'text-green-600')
                                : (livePrice.price < scan.target_price ? 'flash-green-cell-bright' : 'text-red-600')
                            )
                          }`}>
                            {scan.target_price ? `$${scan.target_price.toFixed(2)}` : '-'}
                          </td>
                          {/* Gain % Column */}
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-semibold">
                            {scan.gain_percent != null ? (
                              <span className={scan.gain_percent >= 0 ? 'text-green-600' : 'text-red-600'}>
                                {scan.gain_percent >= 0 ? '+' : ''}{scan.gain_percent.toFixed(2)}%
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                            {scan.risk_reward ? scan.risk_reward.toFixed(2) : '-'}
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                            {getStatusBadge(scan.status)}
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                            <span className="block text-xs sm:text-sm text-gray-900">{scanDate.date}</span>
                            {scanDate.time && (
                              <span className="block text-[10px] sm:text-xs text-gray-500">{scanDate.time}</span>
                            )}
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={editingRows[scan.id]?.executed !== undefined ? editingRows[scan.id].executed : (scan.executed || false)}
                              onChange={(e) => handleExecutedChange(scan.id, e.target.checked)}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                              title={scan.executed ? "Mark as not executed" : "Mark as executed"}
                            />
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                step="0.01"
                                value={editingRows[scan.id]?.profit !== undefined ? editingRows[scan.id].profit : (scan.profit || '')}
                                onChange={(e) => handleProfitChange(scan.id, e.target.value)}
                                placeholder="0.00"
                                className="w-20 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                title="Enter profit/loss amount"
                              />
                              <button
                                onClick={() => handleSaveExecution(scan.id, scan)}
                                disabled={savingRows.has(scan.id)}
                                className={`px-2 py-1 text-xs font-medium rounded transition ${
                                  savingRows.has(scan.id)
                                    ? 'bg-gray-400 text-white cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                                title="Save executed status and profit"
                              >
                                {savingRows.has(scan.id) ? '...' : 'Save'}
                              </button>
                            </div>
                          </td>
                          <td className="px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm">
                            {/* Verify button */}
                            <button
                              onClick={(e) => handleVerifyOne(scan.id, e)}
                              disabled={verifyingScans.has(scan.id)}
                              className={`px-2 sm:px-3 py-1 rounded transition text-xs sm:text-sm mr-2 ${
                                verifyingScans.has(scan.id)
                                  ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                                  : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                              }`}
                              title={verifyingScans.has(scan.id) ? 'Verifying...' : 'Verify this scan against current market data'}
                            >
                              {verifyingScans.has(scan.id) ? '⏳' : '🔍'}
                            </button>
                            {/* Tastytrade button temporarily hidden */}
                            {/* Remove button */}
                            <button
                              onClick={(e) => handleRemove(scan.id, e)}
                              className="text-red-600 hover:text-red-800 hover:bg-red-50 px-2 sm:px-3 py-1 rounded transition text-xs sm:text-sm"
                              title="Remove from view"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalCount > 0 && (
              <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 bg-white border-t border-gray-200 flex-none">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-700">Rows per page:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(parseInt(e.target.value));
                      setPage(1); // Reset to first page
                    }}
                    className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                    <option value="500">500</option>
                    <option value="1000">1000</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(1)}
                    disabled={page === 1 || loading}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="First page"
                  >
                    ⏮️
                  </button>
                  <button
                    onClick={() => setPage(prev => Math.max(1, prev - 1))}
                    disabled={page === 1 || loading}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Previous page"
                  >
                    ◀️ Prev
                  </button>
                  
                  <span className="px-4 py-1 text-sm text-gray-700">
                    Page <span className="font-semibold">{page}</span> of <span className="font-semibold">{Math.ceil(totalCount / pageSize)}</span>
                  </span>
                  
                  <button
                    onClick={() => setPage(prev => prev + 1)}
                    disabled={page >= Math.ceil(totalCount / pageSize) || loading}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Next page"
                  >
                    Next ▶️
                  </button>
                  <button
                    onClick={() => setPage(Math.ceil(totalCount / pageSize))}
                    disabled={page >= Math.ceil(totalCount / pageSize) || loading}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Last page"
                  >
                    ⏭️
                  </button>
                </div>

                <div className="text-sm text-gray-600">
                  Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount}
                </div>
              </div>
            )}
          </div>
          </div>

        </div>
        </div>
      </div>

      {tastyModalScan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-2 sm:px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xl p-5 sm:p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Confirm Tastytrade Order</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Review the option details before sending the order to Tastytrade.
                </p>
              </div>
              <button
                onClick={closeTastyTradeModal}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>

            <div className="mt-4 grid gap-4 text-sm text-gray-700">
              <section className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wide text-gray-500">Contract Specs</span>
                  <span className="text-sm font-semibold text-gray-900">{modalOrderAction}</span>
                </div>
                <div className="mt-3 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Underlying</span>
                    <span className="font-semibold">{tastyModalScan.symbol}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Option Type</span>
                    <span className="font-semibold">{modalOptionType || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Strike (ATM)</span>
                    <span className="font-semibold">
                      {modalEntryPrice ? `${formatCurrency(modalEntryPrice)} • auto` : 'Resolved server-side'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Expiration</span>
                    <span className="font-semibold">Nearest weekly • auto</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Multiplier</span>
                    <span className="font-semibold">×{modalContractMultiplier}</span>
                  </div>
                </div>
              </section>

              <section className="border border-gray-200 rounded-lg p-4">
                <span className="text-xs uppercase tracking-wide text-gray-500">Order Controls</span>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <p className="text-gray-500">Environment</p>
                    <p className="font-semibold capitalize">{tastyEnv}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Order Type</p>
                    <p className="font-semibold">{tastyOrderType}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Time in Force</p>
                    <p className="font-semibold">Day</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Price Effect</p>
                    <p className="font-semibold">{modalPriceEffect}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Limit Price</p>
                    <p className="font-semibold">{modalLimitPrice ? formatCurrency(modalLimitPrice) : tastyOrderType === 'Market' ? 'Market' : '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Live Price</p>
                    <p className="font-semibold">
                      {modalDisplayLivePrice !== null
                        ? formatCurrency(modalDisplayLivePrice)
                        : wsConnected
                          ? '⏳ Loading live...'
                          : '⚠️ Disconnected'}
                    </p>

                    <p className="text-xs text-gray-400 mt-1">
                      Current Volume ({modalTimeframeLabel}):{' '}
                      {modalLiveVolume !== undefined
                        ? formatVolume(modalLiveVolume)
                        : wsConnected ? '⏳ Loading...' : '⚠️ Disconnected'}
                    </p>
                  </div>
                </div>
              </section>

              <section className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wide text-gray-500">Sizing & Levels</span>
                  <span className="text-xs text-gray-400">Single-leg OCC standard</span>
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <p className="text-gray-500">Entry</p>
                    <p className="font-semibold">{formatCurrency(tastyModalScan.entry_price)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Stop</p>
                    <p className="font-semibold">{formatCurrency(tastyModalScan.stop_price)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Target</p>
                    <p className="font-semibold">{formatCurrency(tastyModalScan.target_price)}</p>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-gray-500 text-sm font-medium" htmlFor="scanner-contracts-input">
                      Scanner Contracts
                    </label>
                    <div className="mt-1 flex flex-col gap-1">
                      <input
                        id="scanner-contracts-input"
                        type="number"
                        min="1"
                        step="1"
                        value={manualContractsInput}
                        onChange={(e) => handleManualContractsChange(e.target.value)}
                        className="w-32 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                      <span className="text-xs text-gray-500">Defaults to 1 contract. Adjust as needed.</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-500">Estimated Contracts</p>
                    <p className="font-semibold">{modalDerivedQuantity}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Custom Amount</p>
                    <p className="font-semibold">{parsedCustomAmount ? formatCurrency(parsedCustomAmount) : 'Auto from scanner contracts'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Est. Notional ({modalPriceEffect})</p>
                    <p className="font-semibold">{modalNotional ? formatCurrency(modalNotional) : '-'}</p>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Custom Dollar Amount (optional)
                  </label>
                  <div className="flex flex-col gap-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={customAmountInput}
                      onChange={(e) => setCustomAmountInput(e.target.value)}
                      placeholder="Leave blank to use scanner contracts"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                    {parsedCustomAmount && modalEntryPrice && (
                      <span className="text-xs text-gray-500">
                        ≈ {modalDerivedQuantity} {modalDerivedQuantity === 1 ? 'contract' : 'contracts'} at {formatCurrency(modalEntryPrice)}
                      </span>
                    )}
                    {tastyModalError && (
                      <span className="text-sm text-red-600">{tastyModalError}</span>
                    )}
                  </div>
                </div>
              </section>
            </div>

            <p className="mt-4 text-xs text-gray-500">
              Orders route to Tastytrade as single-leg options using OCC standards (auto-selected ATM strike & nearest expiration). Review sizing before submitting.
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeTastyTradeModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                disabled={tastyTrading}
              >
                Cancel
              </button>
              <button
                onClick={handlePlaceTastyTrade}
                disabled={tastyTrading}
                className="px-4 py-2 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-md disabled:opacity-60"
              >
                {tastyTrading ? 'Sending…' : 'Send Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {signalChartScan && (
        <SignalChartModal
          scan={signalChartScan}
          onClose={closeSignalChartModal}
        />
      )}
    </div>
    </div>
  );
};

export default ScanResultsTable;
